<template>
  <!-- 单个游戏消息组件 -->
  <transition name="game-message-fade" @after-leave="handleAfterLeave">
    <div v-show="visible" class="game-message-item" :class="messageClasses" :style="[customColorStyle, transformStyle]">
      <!-- 消息内容 -->
      <span class="game-message-content" v-html="message"></span>

      <!-- 关闭按钮 -->
      <button v-if="showClose" class="game-message-close" @click="handleClose">
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M13 1L1 13M1 1L13 13"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>
    </div>
  </transition>
</template>

<script setup lang="ts">
import type { MessageType } from './types'

/**
 * Props 定义
 */
interface Props {
  /** 消息唯一标识 */
  id: string
  /** 消息内容 */
  message: string
  /** 消息类型 */
  type: MessageType
  /** 显示时长(毫秒),0 表示不自动关闭 */
  duration: number
  /** 是否显示关闭按钮 */
  showClose: boolean
  /** 自定义颜色 (覆盖 type 默认颜色) */
  customColor?: string
}

const props = withDefaults(defineProps<Props>(), {
  type: 'info',
  duration: 4000,
  showClose: false,
})

/**
 * 获取设备类型 (从全局函数获取)
 */
const getDeviceType = (): string => {
  if (typeof window !== 'undefined' && typeof window.__getDeviceType === 'function') {
    return window.__getDeviceType()
  }
  return 'pc'
}

/**
 * 判断屏幕是否横向
 */
const isLandscape = (): boolean => {
  return window.innerWidth > window.innerHeight
}

// PC 端设计基准尺寸 (与 PcAspectRatioContainer 保持一致)
const DESIGN_WIDTH = 1920
const DESIGN_HEIGHT = 1080

// 窗口尺寸 (响应式)
const windowWidth = ref(window.innerWidth)
const windowHeight = ref(window.innerHeight)

/**
 * 计算 PC 端缩放比例 (与 PcAspectRatioContainer 逻辑一致)
 */
const pcScale = computed(() => {
  const scaleX = windowWidth.value / DESIGN_WIDTH
  const scaleY = windowHeight.value / DESIGN_HEIGHT
  return Math.min(scaleX, scaleY)
})

/**
 * 监听窗口大小变化
 */
const handleResize = () => {
  windowWidth.value = window.innerWidth
  windowHeight.value = window.innerHeight
}

onMounted(() => {
  window.addEventListener('resize', handleResize)
})

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
})

/**
 * 消息样式类
 */
const messageClasses = computed(() => {
  const deviceType = getDeviceType()
  const landscape = isLandscape()
  const isIpad = deviceType === 'ipad'

  return {
    [`game-message-${props.type}`]: true,
    'is-pc': deviceType === 'pc',
    'is-portrait': deviceType === 'mobile-portrait' || (isIpad && !landscape),
    'is-landscape': deviceType === 'mobile-landscape' || (isIpad && landscape),
    'is-ipad': isIpad,
  }
})

/**
 * 消息定位和缩放样式
 * - PC 端: translateX(-50%) + scale
 * - 移动端: translateX(-50%)
 */
const transformStyle = computed(() => {
  const deviceType = getDeviceType()
  if (deviceType === 'pc') {
    return {
      transform: `translateX(-50%) scale(${pcScale.value})`,
      transformOrigin: 'top center',
    }
  }
  // 移动端只需要居中
  return {
    transform: 'translateX(-50%)',
  }
})

/**
 * 自定义颜色样式
 */
const customColorStyle = computed(() => {
  if (props.customColor) {
    return {
      color: props.customColor,
    }
  }
  return undefined
})

/**
 * Emits 定义
 */
const emit = defineEmits<{
  close: [id: string]
  destroy: [id: string]
}>()

/** 是否可见 */
const visible = ref(false)

/** 定时器 */
let timer: ReturnType<typeof setTimeout> | null = null

/**
 * 开始定时器
 */
const startTimer = () => {
  if (props.duration > 0) {
    timer = setTimeout(() => {
      handleClose()
    }, props.duration)
  }
}

/**
 * 清除定时器
 */
const clearTimer = () => {
  if (timer) {
    clearTimeout(timer)
    timer = null
  }
}

/**
 * 关闭消息
 */
const handleClose = () => {
  visible.value = false
  clearTimer()
  emit('close', props.id)
}

/**
 * 离开动画结束后的回调
 */
const handleAfterLeave = () => {
  emit('destroy', props.id)
}

/**
 * 挂载后显示
 */
onMounted(() => {
  visible.value = true
  startTimer()
})

/**
 * 暴露给父组件的方法
 */
defineExpose({
  close: handleClose,
})
</script>

<style scoped lang="scss">
.game-message-item {
  position: fixed;
  top: 10%;
  left: 50%;
  text-align: center;
  background-size: 100% 100%;
  background-repeat: no-repeat;
  background-position: center;
  border: none;
  font-weight: 600;
  z-index: 9999;
  transition:
    opacity 0.3s,
    transform 0.4s;
  pointer-events: auto;
  display: flex;
  align-items: center;
  justify-content: space-between;

  // PC 端: 固定像素尺寸 (在 16:9 容器内会随 transform 缩放)
  &.is-pc {
    min-width: 600px;
    height: 90px;
    border-radius: 4px;
    font-size: 28px;
  }

  // 手机竖屏: 较小尺寸
  &.is-portrait:not(.is-ipad) {
    min-width: 2.08333rem; // 400px / 192
    height: 0.36458rem; // 70px / 192
    border-radius: 0.02083rem;
    font-size: 0.125rem; // 24px / 192
  }

  // 手机横屏: 中等尺寸
  &.is-landscape:not(.is-ipad):not(.is-pc) {
    min-width: 3.125rem; // 600px / 192
    height: 0.52083rem; // 100px / 192
    border-radius: 0.02083rem;
    font-size: 0.1875rem; // 36px / 192
  }

  // iPad 竖屏
  &.is-ipad.is-portrait {
    min-width: 2.6rem;
    height: 0.41667rem;
    border-radius: 0.02083rem;
    font-size: 0.15625rem;
  }

  // iPad 横屏
  &.is-ipad.is-landscape {
    min-width: 3.125rem; // 600px / 192
    height: 0.46875rem; // 90px / 192
    border-radius: 0.02083rem;
    font-size: 0.1875rem; // 36px / 192
  }
}

// Info 类型: 黑金背景 (message2.svg)
.game-message-info {
  background-size: contain;
  background-image: url('@/assets/images/game/message2.svg');
  color: #e8592a;

  .game-message-close {
    color: #e8592a;

    &:hover {
      color: #ffed4a;
    }
  }
}

// Error 类型: 红色背景 (message1.svg)
.game-message-error {
  background-size: contain;
  background-image: url('@/assets/images/game/message1.svg');
  color: #ffb3b3;

  .game-message-close {
    color: #ffb3b3;

    &:hover {
      color: #ff8282;
    }
  }
}

// Success 和 Warning 使用默认样式
.game-message-success {
  background: linear-gradient(90deg, #67c23a 0%, #85ce61 100%);
  // box-shadow: 0 4px 20px rgba(103, 194, 58, 0.3);
  color: #fff;
}

.game-message-warning {
  background: linear-gradient(90deg, #e6a23c 0%, #f0c78a 100%);
  color: #fff;
}

// 消息内容
.game-message-content {
  flex: 1;
}

// 关闭按钮
.game-message-close {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.3s;

  &:hover {
    opacity: 0.7;
  }
}

// PC 端关闭按钮
.game-message-item.is-pc .game-message-close {
  right: 15px;
  padding: 5px;
}

// 移动端/iPad 关闭按钮
.game-message-item:not(.is-pc) .game-message-close {
  right: 0.07813rem; // 15px / 192
  padding: 0.02604rem; // 5px / 192
}

// 淡入淡出动画
.game-message-fade-enter-active {
  transition:
    opacity 0.3s,
    transform 0.4s;
}

.game-message-fade-leave-active {
  transition:
    opacity 0.2s,
    transform 0.3s;
}

// 注意: 进入/离开动画的 translateX(-50%) 由行内样式 transformStyle 提供
// 这里只控制 opacity，避免与动态 transform 冲突
.game-message-fade-enter-from {
  opacity: 0;
}

.game-message-fade-leave-to {
  opacity: 0;
}
</style>
